<script setup lang="ts">
import { ref, computed, watch, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuery, useMutation } from '@/composables/api'
import { useAccess } from '@/composables/useAccess'
import { useAuth } from '@/composables/useAuth.ts'
import type { MutationFunctionContext } from '@tanstack/vue-query'
import BDialog from '@/components/base/BDialog/BDialog.vue'
import mdiCheck from '~icons/mdi/check'

interface CreditorData {
  external_id?: string
  name?: string
  already_registered?: boolean
}

const showDialog = defineModel<boolean>({ required: true })

const { t } = useI18n()
const formId = useId()
const { canAccessResource } = useAccess()
const { isSolicitor, isSystemUser } = useAuth()

const selectedCreditorId = ref<string | null>(null)
const registeredCreditor = ref<CreditorData | null>(null)
const showSummary = computed(() => registeredCreditor.value != null)

const canAccessImportFilters = canAccessResource('partner--import-filters')

const registrationDataQuery = useQuery(
  '/visma-net-registration--data',
  {},
  {
    enabled: computed(() => !isSolicitor.value && !isSystemUser.value && canAccessImportFilters.value),
  }
)

const isLoading = computed(() => registrationDataQuery.isLoading.value)

const creditors = computed<CreditorData[]>(() => {
  const data = registrationDataQuery.data.value
  if (data == null) return []

  const creditorsData = data.creditors_data
  const alreadyRegistered = data.creditors_already_registered ?? []

  const registeredIds = new Set<string | undefined>()
  for (const c of alreadyRegistered) {
    registeredIds.add(c.external_id)
  }

  const result: CreditorData[] = []
  for (const creditor of creditorsData) {
    result.push({
      ...creditor,
      already_registered: registeredIds.has(creditor.external_id),
    })
  }
  return result
})

const hasMultipleCreditors = computed(() => creditors.value.length > 1)
const singleCreditor = computed(() => creditors.value[0])

const registerMutation = useMutation('post', '/visma-net-registration--register', {
  onSuccess: async (_data: unknown, _vars: unknown, _result: unknown, ctx: MutationFunctionContext) => {
    await Promise.all([
      ctx.client.invalidateQueries({ queryKey: ['get', '/partner--import-filters'] }),
      ctx.client.invalidateQueries({ queryKey: ['get', '/visma-net-registration--data'] }),
    ])
  },
})
const isPending = computed(() => registerMutation.isPending.value)

async function handleRegister() {
  if (selectedCreditorId.value == null) return

  const data = registrationDataQuery.data.value
  const token = data?.token
  if (token == null) return

  await registerMutation.mutateAsync({
    // @ts-expect-error Swagger lies
    body: {
      token,
      external_creditors: [{ external_id: selectedCreditorId.value }],
    },
  })

  const registered = creditors.value.find((c) => c.external_id === selectedCreditorId.value)
  if (registered != null) {
    registeredCreditor.value = registered
  }
}

function resetState() {
  registeredCreditor.value = null
  selectedCreditorId.value = null
}

watch(
  creditors,
  (newCreditors) => {
    if (selectedCreditorId.value != null) return
    if (showSummary.value) return
    const firstAvailable = newCreditors.find((c) => c.already_registered !== true)
    if (firstAvailable?.external_id != null) {
      selectedCreditorId.value = firstAvailable.external_id
    }
  },
  { immediate: true }
)
</script>

<template>
  <b-dialog
    v-model="showDialog"
    size="medium"
    @after-leave="resetState"
  >
    <template #title>
      {{ showSummary ? t('common.summary') : t('common.register_new_creditor') }}
    </template>

    <v-card-text v-if="showSummary">
      <h3 class="text-headline-small mb-4">{{ t('common.new_creditors') }}</h3>

      <v-card
        variant="outlined"
        class="pa-4"
      >
        <v-list class="py-0">
          <v-list-item class="px-0">
            <v-list-item-title class="font-weight-bold">
              {{ registeredCreditor?.name }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ t('common.creditor_external_id') }}: {{ registeredCreditor?.external_id }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-card>
    </v-card-text>

    <v-card-text v-else>
      <div
        v-if="isLoading"
        class="d-flex justify-center py-4"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>

      <template v-else>
        <v-card
          v-if="!hasMultipleCreditors && singleCreditor"
          variant="outlined"
        >
          <v-card-title class="text-body-large pb-0">{{ singleCreditor.name }}</v-card-title>
          <v-card-subtitle class="pb-2">
            {{ t('common.creditor_external_id') }}: {{ singleCreditor.external_id }}
          </v-card-subtitle>
        </v-card>

        <v-form
          v-else
          :id="formId"
          @submit.prevent="handleRegister"
        >
          <div class="text-headline-small mb-4">
            {{ t('common.select_creditor') }}
          </div>

          <v-card
            variant="outlined"
            class="overflow-y-auto"
            max-height="400"
          >
            <v-radio-group
              v-model="selectedCreditorId"
              hide-details
            >
              <v-list
                class="py-0"
                lines="two"
              >
                <v-list-item
                  v-for="(creditor, index) in creditors"
                  :key="creditor.external_id ?? index"
                  :disabled="creditor.already_registered"
                  @click="
                    () => {
                      if (creditor.already_registered) return
                      selectedCreditorId = creditor.external_id ?? null
                    }
                  "
                >
                  <template #prepend>
                    <v-radio
                      v-if="!creditor.already_registered"
                      :value="creditor.external_id"
                      color="brand-primary"
                      hide-details
                    />
                    <v-icon
                      v-else
                      :icon="mdiCheck"
                      color="success"
                      class="ml-2"
                    />
                  </template>

                  <v-list-item-title>{{ creditor.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ t('common.creditor_external_id') }}: {{ creditor.external_id }}
                  </v-list-item-subtitle>

                  <template
                    v-if="creditor.already_registered"
                    #append
                  >
                    <span class="text-body-medium text-medium-emphasis">
                      {{ t('common.already_registered') }}
                    </span>
                  </template>
                </v-list-item>
              </v-list>
            </v-radio-group>
          </v-card>
        </v-form>
      </template>
    </v-card-text>

    <template #footer>
      <template v-if="showSummary">
        <v-btn
          color="primary"
          variant="flat"
          @click="showDialog = false"
        >
          {{ t('common.ok') }}
        </v-btn>
      </template>
      <template v-else>
        <v-btn
          variant="text"
          color="primary"
          @click="showDialog = false"
        >
          {{ t('common.cancel') }}
        </v-btn>
        <v-btn
          :disabled="selectedCreditorId == null || isPending"
          :loading="isPending"
          :form="formId"
          type="submit"
          color="primary"
          variant="flat"
        >
          {{ t('common.register') }}
        </v-btn>
      </template>
    </template>
  </b-dialog>
</template>

<style scoped>
:deep(.v-list-item__prepend > .v-radio ~ .v-list-item__spacer) {
  width: var(--v-list-prepend-gap, 24px);
}
</style>
